<script setup lang="ts">
import { getDayOfWeek, parseDate, parseTime, type Time } from "@internationalized/date"
import type { Location, Service } from "@mmh/types"
import { InquiryType } from "@mmh/types/constants"
import { toTypedSchema } from "@vee-validate/zod"
import { createReusableTemplate } from "@vueuse/core"
import { useRouteQuery } from "@vueuse/router"
import { CalendarIcon, ChevronDown, Clock, MapPin, UsersRound, X } from "lucide-vue-next"
import { useForm } from "vee-validate"
import type { HTMLAttributes } from "vue"
import { nextTick, useTemplateRef } from "vue"
import { z } from "zod"
import { InquiryDialog } from "~/components/inquiry"
import { ThemeButton } from "~/components/theme"
import { Calendar } from "~/components/ui/calendar"
import { Collapsible, CollapsibleContent } from "~/components/ui/collapsible"
import { Dialog, DialogContent, DialogTrigger } from "~/components/ui/dialog"
import { Drawer, DrawerClose, DrawerContent, DrawerTrigger } from "~/components/ui/drawer"
import { FormControl, FormField, FormItem } from "~/components/ui/form"
import Popover from "~/components/ui/popover/Popover.vue"
import PopoverContent from "~/components/ui/popover/PopoverContent.vue"
import PopoverTrigger from "~/components/ui/popover/PopoverTrigger.vue"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select"
import { Separator } from "~/components/ui/separator"
import { useConcept } from "~/composables/concept"
import { useLocation } from "~/composables/locations"
import { useMedia } from "~/composables/media"
import { useServiceFiltering } from "~/composables/serviceFiltering"
import { useTime } from "~/composables/time"
import { titleCase } from "~/lib/case"
import { cn } from "~/lib/utils"
import ReservationInfoPopover from "./ReservationInfoPopover.vue"

const { class: cl } = defineProps<{
    class?: HTMLAttributes["class"]
}>()

const { isDesktop } = useMedia()
const open = defineModel("open", { type: Boolean, default: false })
const modal = useRouteQuery("modal")

const { concept } = useConcept()
const { location } = useLocation()
const { currentDate, currentTime, formatDate, formatTime, isDateDisabled: _isDateDisabled } = useTime("America/Los_Angeles")

const isDateDisabled = (date: any) => {
    if (_isDateDisabled(date)) return true
    if (concept.value?.disabledDates?.reservation?.some(
        (d: { month: number; day: number }) => d.month === date.month && d.day === date.day
    )) return true
    if (inquiryRequired.value && concept.value?.disabledDates?.inquiry?.some(
        (d: { month: number; day: number }) => d.month === date.month && d.day === date.day
    )) return true
    return false
}

const calendarRange = 31
const calendarOpen = ref(false)
const inquiryOpen = ref(false)

const formSchema = toTypedSchema(z.object({
    locationName: z.string(),
    guests: z.number(),
    date: z.string(),
    time: z.string()
}))

const form = useForm({ 
    validationSchema: formSchema,
    initialValues: {
        locationName: location.value?.name || ""
    }
})

const selectedLocation = computed(() => location.value || concept.value.locations.find((location: Location) => location.name === form.values.locationName))
const selectedGuests = computed(() => form.values.guests)
const selectedDate = computed(() => form.values.date ? parseDate(form.values.date) : undefined)
const selectedTime = computed(() => form.values.time ? parseTime(form.values.time) : "")
const selectedDay = computed(() => {
    if (!selectedDate.value) return undefined
    return getDayOfWeek(selectedDate.value, "en-US")}
)
const selectedOpenClose = computed((): [Time, Time] | null => {
    if (!selectedLocation.value || selectedDay.value === undefined) return null
    
    const bookableServices = selectedLocation.value.services.filter((service: Service) => service.bookable !== false)
    const timeRanges = []
    
    for (const service of bookableServices) {
        for (const hours of service?.hours || []) {
            if (hours.days.includes(selectedDay.value)) {
                timeRanges.push({
                    start: parseTime(hours.times[0]),
                    end: parseTime(hours.times[1])
                })
            }
        }
    }
    
    if (timeRanges.length === 0) return null
    
    const earliestStart = timeRanges.reduce((earliest, range) => 
        range.start.compare(earliest) < 0 ? range.start : earliest, 
    timeRanges[0].start
    )
    
    const latestEnd = timeRanges.reduce((latest, range) => 
        range.end.compare(latest) > 0 ? range.end : latest, 
    timeRanges[0].end
    )
    
    return [earliestStart, latestEnd]
})
const resyThreshold = computed(() => {
    return selectedLocation.value?.services.find((s: Service) => s.key === "dinner")?.prixFixeThreshold
})

const isSelectedDateInquiryBlocked = computed(() => {
    if (!selectedDate.value) return false
    return concept.value?.disabledDates?.inquiry?.some(
        (d: { month: number; day: number }) => d.month === selectedDate.value!.month && d.day === selectedDate.value!.day
    ) ?? false
})

const selectedGuestOptions = computed(() => {
    const options = []
    if (resyThreshold.value) {
        const max = isSelectedDateInquiryBlocked.value ? resyThreshold.value - 1 : resyThreshold.value
        for (let i = 1; i <= max; i++) {
            options.push(i)
        }
    }
    
    return options
})

const inquiryRequired = computed(() => {
    if (!selectedGuests.value) return false
    return !!resyThreshold.value && selectedGuests.value >= resyThreshold.value
})

const { timeOptions, calendarMinDate } = useServiceFiltering({
    location: selectedLocation,
    currentDate,
    currentTime,
    selectedDate,
    selectedDay,
    selectedOpenClose
})

const showNeedToKnow = ref(false)
const needToKnow = computed(() => {
    if (!selectedLocation.value) {
        return []
    }
    if (inquiryRequired.value) {
        return selectedLocation.value.needToKnow?.reservationInquiry || []
    }
    return selectedLocation.value.needToKnow?.reservationTable || []
})

const submitText = computed(() => {
    if (inquiryRequired.value) {
        return "Inquire Now"
    } else { 
        return "Book Now"
    }
})

watch(currentDate, (value, oldValue) => {
    if (!selectedDate.value) {
        form.resetField("date", { value: "" })
    } else if (value && oldValue && selectedDate.value && selectedDate.value.compare(oldValue) === 0) {
        form.resetField("date", { value: value.toString() })
    }
})

watch(currentTime, (value, oldValue) => {
    if (value && oldValue && selectedTime.value && selectedTime.value.compare(oldValue) === 0) {
        form.resetField("time", { value: value.toString() })
    }
})

watch(selectedGuestOptions, (value) => {
    if (value.length) {
        const first = value[0]
        const last = value[value.length - 1]
        if (!selectedGuests.value) {
            form.resetField("guests", { value: undefined })
        } else if (selectedGuests.value < first) {
            form.resetField("guests", { value: undefined })
        } else if (selectedGuests.value > last) {
            form.resetField("guests", { value: undefined })
        }
    }
}, { immediate: true })

watch(timeOptions, (value) => {
    if (value.length && selectedTime.value) {
        const first = value[0]
        const last = value[value.length - 1]
        if (!(selectedTime.value.compare(first) >= 0)) {
            form.resetField("time", { value: first.toString() })
        } else if (!(selectedTime.value.compare(last) <= 0)) {
            form.resetField("time", { value: last.toString() })
        }
    }
}, { immediate: true })

watch(modal, value => {
    if (value === "reservations") {
        open.value = true
        form.resetField("locationName", { value: location.value?.name || "" })
    } else {
        open.value = false
    }
}, { immediate: true })

watch(open, (value, oldValue) => {
    // Only reset form when closing (value changes from true to false)
    // Don't run on initial mount (oldValue === undefined)
    if (oldValue !== undefined && !value) {
        form.resetForm({
            values: {
                locationName: location.value?.name || "",
                guests: undefined,
                date: "",
                time: ""
            }
        })
        modal.value = undefined
    } else {
        nextTick(() => {
            const target = window.document.querySelector("[data-reservation-initial-focus]") as HTMLElement | null
            target?.focus()
        })
    }
})

// PATCH: While we figure out how to avoid poppup blocking in common browsers, we'll bump them to a new tab
const buildResyUrl = () => {
    let baseUrl

    if (location.value?.external?.api?.resy) {
        baseUrl = location.value?.external?.api?.resy?.url
    } else if (selectedLocation.value?.external?.api?.resy) {
        baseUrl = selectedLocation.value?.external?.api?.resy?.url
    } else {
        baseUrl = `https://resy.com/venues/${location.value?.external?.api?.resy?.venueId || ""}`
    }

    const params = new URLSearchParams({
        date: selectedDate.value ? selectedDate.value.toString() : currentDate.value.toString(),
        seats: selectedGuests.value ? selectedGuests.value.toString() : "2"
    })
    
    return `${baseUrl}?${params.toString()}`
}

const buildOpenTableUrl = () => {
    let baseUrl
    
    if (location.value?.external?.api?.openTable?.url) {
        baseUrl = location.value?.external?.api?.openTable?.url
    } else if (selectedLocation.value?.external?.api?.openTable?.url) {
        baseUrl = selectedLocation.value?.external?.api?.openTable?.url
    }
    
    if (!baseUrl) {
        console.warn("No OpenTable URL found")
        return ""
    }
    
    const date = selectedDate.value ? selectedDate.value.toString() : currentDate.value.toString()
    const time = selectedTime.value ? selectedTime.value.toString() : "19:00"
    const guestCount = selectedGuests.value ? selectedGuests.value.toString() : "2"
    
    const dateTime = `${date}T${time}`.replace(":", "%3A")
    
    return `${baseUrl}?covers=${guestCount}&dateTime=${dateTime}`
}

const handleSubmit = form.handleSubmit(() => {
    if (inquiryRequired.value) {
        inquiryOpen.value = true
    } else if (selectedLocation.value?.external?.api?.resy) {
        const resyUrl = buildResyUrl()
        if (typeof window !== "undefined") {
            window.open(resyUrl, "_blank", "noopener,noreferrer")
        }
        open.value = false
    } else if (selectedLocation.value?.external?.api?.openTable) {
        const openTableUrl = buildOpenTableUrl()
        if (typeof window !== "undefined") {
            window.open(openTableUrl, "_blank", "noopener,noreferrer")
        }
        open.value = false
    } else {
        console.error("No reservation provider found for location", selectedLocation.value)
    }
})

const handleCancel = () => {
    form.resetForm({
        values: {
            locationName: location.value?.name || "",
            guests: undefined,
            date: "",
            time: ""
        }
    })
    open.value = false
}

const submitButton = useTemplateRef("submitButton")

const [ReservationFormTemplate, ReservationForm] = createReusableTemplate()
</script>

<template>
    <ReservationFormTemplate>
        <div class="w-full space-y-8">
            <InquiryDialog
                v-if="selectedLocation && selectedGuests && selectedDate && selectedTime && form.values.date && form.values.time"
                v-model:open="inquiryOpen"
                :type="InquiryType.LargeParty"
                :location="selectedLocation"
                :date="selectedDate"
                :time="selectedTime"
                trigger-type="reservation_dialog"
                @success="open = false"
                @cancel="handleCancel"
            />
            <header class="space-y-4 text-center">
                <h3 class="text-xl md:text-2xl text-reservation-form-title font-headline">
                    Reservations
                </h3>
                <Separator class="w-10 mx-auto bg-background" />
            </header>
            <form
                class="flex flex-col items-center justify-center gap-6"
                @submit="handleSubmit"
            >
                <div class="w-full grid gap-2">
                    <FormField
                        v-if="!location"
                        v-slot="{ value, handleChange }"
                        name="locationName"
                    >
                        <FormItem class="[&>svg]:hidden inline-flex w-full">
                            <Select
                                :model-value="value"
                                @update:model-value="handleChange($event)"
                            >
                                <FormControl>
                                    <SelectTrigger
                                        as-child
                                    >
                                        <ThemeButton
                                            size="lg"
                                            tabindex="0"
                                            class="h-11 justify-between gap-4 border-transparent bg-reservation-form-item-background hover:bg-reservation-form-item-hover text-reservation-form-text"
                                        >
                                            <div class="w-full flex items-center gap-4">
                                                <MapPin class="size-4 text-reservation-form-text" />
                                                <SelectValue
                                                    placeholder="Select Location"
                                                    class="data-[placeholder]:text-reservation-form-text capsize"
                                                />
                                            </div>
                                            <ChevronDown class="size-4 text-reservation-form-text" />
                                        </ThemeButton>
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent class="max-h-64 border-none">
                                    <SelectGroup>
                                        <SelectItem
                                            v-for="l of concept.locations.filter((l: Location) => !l.closed)"
                                            :key="l.name"
                                            :value="l.name"
                                        >
                                            {{ titleCase(l.name) }}
                                        </SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        </FormItem>
                    </FormField>
                    <FormField
                        v-slot="{ value, handleChange }"
                        name="guests"
                    >
                        <FormItem class="[&>svg]:hidden inline-flex w-full">
                            <Select
                                :model-value="value?.toString()"
                                @update:model-value="handleChange(parseInt($event as string))"
                            >
                                <FormControl>
                                    <SelectTrigger
                                        as-child
                                    >
                                        <ThemeButton
                                            size="lg"
                                            tabindex="0"
                                            class="h-11 justify-between gap-4 border-transparent bg-reservation-form-item-background hover:bg-reservation-form-item-hover text-reservation-form-text"
                                            :disabled="!location && !selectedLocation"
                                            data-reservation-initial-focus
                                        >
                                            <div class="w-full flex items-center gap-4">
                                                <UsersRound class="size-4 text-reservation-form-text" />
                                                <SelectValue
                                                    placeholder="Select Party Size"
                                                    class="data-[placeholder]:text-reservation-form-text capsize"
                                                />
                                            </div>
                                            <ChevronDown class="size-4 text-reservation-form-text" />
                                        </ThemeButton>
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent class="max-h-64 border-none">
                                    <SelectGroup>
                                        <SelectItem
                                            v-for="s of selectedGuestOptions"
                                            :key="s.toString()"
                                            :value="s.toString()"
                                        >
                                            {{ s }}{{ s === selectedGuestOptions[selectedGuestOptions.length - 1] && !isSelectedDateInquiryBlocked ? "+" : "" }} {{ s === 1 ? "Guest" : "Guests" }}
                                        </SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        </FormItem>
                    </FormField>
                    <FormField
                        v-slot="{ value, handleChange }"
                        name="date"
                    >
                        <FormItem class="[&>svg]:hidden inline-flex w-full text-reservation-form-text">
                            <Popover v-model:open="calendarOpen">
                                <FormControl>
                                    <PopoverTrigger as-child>
                                        <ThemeButton
                                            size="lg"
                                            tabindex="0"
                                            class="px-3 w-full h-11 justify-between gap-4 bg-reservation-form-item-background hover:bg-reservation-form-item-hover text-reservation-form-text"
                                            :disabled="!selectedLocation || !selectedGuests"
                                        >
                                            <div class="w-full flex items-center gap-4">
                                                <CalendarIcon class="size-4 text-reservation-form-text" />
                                                <span class="capsize text-reservation-form-text">{{ value ? formatDate(value) : 'Select Date' }}</span>
                                            </div>
                                            <ChevronDown class="size-4 text-reservation-form-text" />
                                        </ThemeButton>
                                    </PopoverTrigger>
                                </FormControl>
                                <PopoverContent class="p-0 border-none text-reservation-form-calendar-text">
                                    <Calendar
                                        class="reservation-calendar bg-reservation-form-background"
                                        :min-value="calendarMinDate"
                                        :max-value="currentDate.add({ days: calendarRange })"
                                        :model-value="selectedDate"
                                        :is-date-disabled="isDateDisabled"
                                        @update:model-value="handleChange($event?.toString()); calendarOpen = false"
                                    />
                                </PopoverContent>
                            </Popover>
                        </FormItem>
                    </FormField>
                    <FormField
                        v-slot="{ value, handleChange }"
                        name="time"
                    >
                        <FormItem class="[&>svg]:hidden inline-flex w-full">
                            <Select
                                :model-value="value"
                                @update:model-value="handleChange"
                            >
                                <FormControl>
                                    <SelectTrigger
                                        as-child
                                    >
                                        <ThemeButton
                                            size="lg"
                                            tabindex="0"
                                            class="h-11 justify-between gap-4 border-transparent bg-reservation-form-item-background hover:bg-reservation-form-item-hover text-reservation-form-text"
                                            :disabled="!selectedLocation || !selectedGuests || !selectedDate"
                                        >
                                            <div class="w-full flex items-center gap-4">
                                                <Clock class="size-4 text-reservation-form-text" />
                                                <SelectValue
                                                    placeholder="Select Time"
                                                    class="data-[placeholder]:text-reservation-form-text capsize"
                                                />
                                            </div>
                                            <ChevronDown class="size-4 text-reservation-form-text" />
                                        </ThemeButton>
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent class="max-h-64 border-none">
                                    <SelectGroup>
                                        <SelectItem
                                            v-for="t of timeOptions"
                                            :key="t.toString()"
                                            :value="t.toString()"
                                        >
                                            {{ formatTime(t.toString()) }}
                                        </SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        </FormItem>
                    </FormField>
                    <Collapsible :open="inquiryRequired">
                        <CollapsibleContent class="text-sm text-center p-4 bg-reservation-collapsible-background text-reservation-collapsible-text">
                            {{ location?.inquiryMessage || selectedLocation?.inquiryMessage }}
                        </CollapsibleContent>
                    </Collapsible>
                </div>
                <ReservationInfoPopover 
                    v-model:open="showNeedToKnow" 
                    :info="needToKnow"
                    @confirm="submitButton?.$el.click()"
                >
                    <ThemeButton
                        ref="submitButton"
                        :disabled="!selectedLocation || !selectedGuests || !selectedDate || !selectedTime"
                        type="submit"
                        size="lg"
                        tabindex="0"
                        class="w-full bg-reservation-form-submit text-reservation-form-submit-text hover:bg-reservation-form-submit-hover min-w-40 transition-colors duration-200"
                        @click="(e: MouseEvent) => {
                            if (!(inquiryRequired || showNeedToKnow)) {
                                e.preventDefault()
                                showNeedToKnow = !inquiryRequired
                            } else {
                                showNeedToKnow = false
                            }
                        }"
                    >
                        {{ submitText }}
                    </ThemeButton>
                </ReservationInfoPopover>
            </form>
        </div>
    </ReservationFormTemplate>
    <Dialog 
        v-if="isDesktop"
        v-model:open="open"
    >
        <DialogTrigger as-child>
            <slot />
        </DialogTrigger>
        <DialogContent
            :class="cn(
                'size-full max-w-none bg-reservation-dialog-background backdrop-blur-sm sm:rounded-none border-none text-reservation-form-text [&>button:focus-visible]:outline-foreground',
                cl
            )"
        >
            <div class="size-full max-w-md mx-auto flex flex-col items-center justify-center p-6">
                <ReservationForm />
            </div>
        </DialogContent>
    </Dialog>
    <Drawer
        v-else
        v-model:open="open"
    >
        <DrawerTrigger as-child>
            <slot />
        </DrawerTrigger>
        <DrawerContent
            :class="cn('max-h-[95svh] [&>:first-child]:shrink-0 bg-reservation-dialog-background-mobile text-background backdrop-blur-sm border-none', cl)"
        >
            <DrawerClose
                class="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Close dialog"
            >
                <X class="size-4" />
            </DrawerClose>
            <div class="size-full max-h-full overflow-y-auto">
                <div class="flex items-center justify-center p-4">
                    <ReservationForm />
                </div>
            </div>
        </DrawerContent>
    </Drawer>
</template>

<style scoped>
.reservation-calendar {
    --muted-foreground: var(--reservation-form-calendar-text);
    --bg-muted: var(--reservation-form-calendar-text);
}
</style>
